"use client";

import {
	useState,
	useRef,
	useCallback,
	useEffect,
	useLayoutEffect,
	type ReactNode,
	type CSSProperties,
} from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";
import { navigationExitDuration } from "./navigation-motion";

type PanelId = "peptides" | "research" | "support";

interface MegaMenuProps {
	triggers: { id: PanelId; label: string }[];
	directLinks?: { href: string; label: string }[];
	children: (activePanel: PanelId | null) => ReactNode;
}

const PANEL_WIDTHS: Record<PanelId, string> = {
	peptides: "w-[880px]",
	research: "w-[880px]",
	support: "w-[420px]",
};

export function MegaMenu({ triggers, directLinks, children }: MegaMenuProps) {
	const [activePanel, setActivePanel] = useState<PanelId | null>(null);
	const [renderedPanel, setRenderedPanel] = useState<PanelId | null>(null);
	const [placement, setPlacement] = useState({ shift: 0, arrow: 0 });
	const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const triggerRefs = useRef<Partial<Record<PanelId, HTMLButtonElement | null>>>({});
	const positionRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const cancelIntentTimers = useCallback(() => {
		if (openTimer.current) clearTimeout(openTimer.current);
		if (closeTimer.current) clearTimeout(closeTimer.current);
		openTimer.current = null;
		closeTimer.current = null;
	}, []);

	const showPanel = useCallback(
		(panel: PanelId) => {
			cancelIntentTimers();
			if (exitTimer.current) clearTimeout(exitTimer.current);
			exitTimer.current = null;
			setRenderedPanel(panel);
			setActivePanel(panel);
		},
		[cancelIntentTimers],
	);

	const closePanel = useCallback(
		(returnFocus = false) => {
			cancelIntentTimers();
			if (exitTimer.current) clearTimeout(exitTimer.current);
			if (returnFocus) {
				const trigger = activePanel && triggerRefs.current[activePanel];
				trigger?.focus();
			}
			setActivePanel(null);
			const duration = navigationExitDuration();
			if (duration === 0) {
				setRenderedPanel(null);
				return;
			}
			exitTimer.current = setTimeout(() => {
				setRenderedPanel(null);
				exitTimer.current = null;
			}, duration);
		},
		[activePanel, cancelIntentTimers],
	);

	const scheduleOpen = useCallback(
		(panel: PanelId) => {
			cancelIntentTimers();
			if (exitTimer.current) clearTimeout(exitTimer.current);
			exitTimer.current = null;
			openTimer.current = setTimeout(() => showPanel(panel), 150);
		},
		[cancelIntentTimers, showPanel],
	);

	const closeWithGrace = useCallback(() => {
		cancelIntentTimers();
		closeTimer.current = setTimeout(() => {
			if (panelRef.current?.contains(document.activeElement)) return;
			closePanel();
		}, 120);
	}, [cancelIntentTimers, closePanel]);

	// Move the center just enough for the arrow to stay within the rounded edge.
	// Its offset then remains stable throughout the surface's width transition.
	useLayoutEffect(() => {
		if (!activePanel) return;
		const updatePlacement = () => {
			const trigger = triggerRefs.current[activePanel];
			const position = positionRef.current;
			if (!trigger || !position) return;
			const triggerRect = trigger.getBoundingClientRect();
			const containingRect =
				position.offsetParent?.getBoundingClientRect() ?? position.getBoundingClientRect();
			const baseCenter = containingRect.left + containingRect.width / 2;
			const targetCenter = triggerRect.left + triggerRect.width / 2;
			const width = Math.min(activePanel === "support" ? 420 : 880, window.innerWidth - 32);
			const arrowEdge = 28;
			const arrowAlignedCenter = Math.max(
				targetCenter - width / 2 + arrowEdge,
				Math.min(baseCenter, targetCenter + width / 2 - arrowEdge),
			);
			const center = Math.max(
				width / 2 + 16,
				Math.min(arrowAlignedCenter, window.innerWidth - width / 2 - 16),
			);
			setPlacement({ shift: center - baseCenter, arrow: targetCenter - center });
		};
		updatePlacement();
		window.addEventListener("resize", updatePlacement);
		return () => window.removeEventListener("resize", updatePlacement);
	}, [activePanel]);

	useEffect(() => {
		if (!activePanel) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;
			event.preventDefault();
			closePanel(true);
		};
		const onPointerDown = (event: PointerEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) closePanel();
		};
		document.addEventListener("keydown", onKey);
		document.addEventListener("pointerdown", onPointerDown);
		return () => {
			document.removeEventListener("keydown", onKey);
			document.removeEventListener("pointerdown", onPointerDown);
		};
	}, [activePanel, closePanel]);

	useEffect(
		() => () => {
			cancelIntentTimers();
			if (exitTimer.current) clearTimeout(exitTimer.current);
		},
		[cancelIntentTimers],
	);

	return (
		<div
			ref={containerRef}
			className="hidden min-w-0 lg:flex lg:items-center"
			onMouseLeave={closeWithGrace}
			onBlur={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget)) closePanel();
			}}
		>
			<nav className="flex items-center gap-1 text-[13.5px] font-medium" role="navigation" aria-label="Main">
				{triggers.map(({ id, label }) => {
					const isOpen = activePanel === id;
					return (
						<button
							key={id}
							ref={(element) => {
								triggerRefs.current[id] = element;
							}}
							type="button"
							className={cn(
								"text-foreground/85 inline-flex items-center gap-1.5 rounded-full px-3 py-2 transition-colors duration-150",
								"hover:bg-foreground/[0.06] hover:text-foreground",
								isOpen && "bg-foreground/[0.06] text-foreground",
							)}
							onMouseEnter={() => scheduleOpen(id)}
							onClick={() => (isOpen ? closePanel() : showPanel(id))}
							aria-expanded={isOpen}
							aria-haspopup="true"
							aria-controls="header-mega-menu"
						>
							{label}
							<ChevronDown
								className={cn("h-3 w-3 transition-transform duration-200", isOpen && "rotate-180")}
							/>
						</button>
					);
				})}
				{directLinks?.map(({ href, label }) => (
					<LinkWithChannel
						key={href}
						href={href}
						className="text-foreground/85 hover:bg-foreground/[0.06] rounded-full px-3 py-2 transition-colors hover:text-foreground"
					>
						{label}
					</LinkWithChannel>
				))}
			</nav>

			{renderedPanel ? (
				<div
					ref={positionRef}
					className="t-nav-mega-position absolute top-[calc(100%+10px)] z-50 -translate-x-1/2"
					style={{ "--panel-shift": `${placement.shift}px` } as CSSProperties}
				>
					<div
						id="header-mega-menu"
						ref={panelRef}
						inert={!activePanel}
						aria-hidden={!activePanel}
						onMouseEnter={cancelIntentTimers}
						onMouseLeave={closeWithGrace}
						onClickCapture={(event) => {
							if (!activePanel) {
								event.preventDefault();
								event.stopPropagation();
							}
						}}
						onKeyDownCapture={(event) => {
							if (!activePanel) {
								event.preventDefault();
								event.stopPropagation();
							}
						}}
						className={cn(
							"t-nav-mega-surface t-nav-dropdown border-foreground/[0.08] max-w-[calc(100vw-2rem)] rounded-[28px] border bg-card p-[26px] shadow-[0_24px_60px_rgba(14,23,38,0.14),0_2px_6px_rgba(14,23,38,0.06)]",
							activePanel ? "is-open" : "is-closing",
							PANEL_WIDTHS[renderedPanel],
						)}
						data-origin="top-center"
						style={{ "--arrow-offset": `${placement.arrow}px` } as CSSProperties}
					>
						<span
							aria-hidden
							className="t-nav-mega-arrow border-foreground/[0.08] absolute -top-[7px] h-[14px] w-[14px] border-l border-t bg-card"
						/>
						<div className="overflow-hidden">{children(renderedPanel)}</div>
					</div>
				</div>
			) : (
				children(null)
			)}
		</div>
	);
}
